import { useQueryClient } from '@tanstack/react-query';
import { useTopic } from './useWebSocket';
import { queryKeys } from '@/app/queryClient';
import { useNotificationStore } from '@/app/notificationStore';

interface OrderMessage {
  type: string;
  id: number;
  orderNumber: string;
  status: string;
  changedBy: string;
  summary: string;
}

interface PriceMessage {
  type: string;
  ingredientId: number;
  ingredientName: string;
  newPrice: string;
  affectedDishIds: number[];
}

/**
 * Wires server events into the query cache.
 *
 * Rather than refetching everything on any event, each message invalidates only the keys
 * it can actually have changed, so an unrelated screen is not disturbed.
 */
export const useRealtimeSync = () => {
  const queryClient = useQueryClient();
  const notify = useNotificationStore((state) => state.push);

  useTopic('/topic/orders', (payload) => {
    const message = payload as OrderMessage;
    void queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
    if (message.type === 'STATUS_CHANGED') {
      notify({
        severity: 'info',
        messageKey: 'realtime.orderStatusChanged',
        values: { number: message.orderNumber, status: message.status },
      });
    }
  });

  useTopic('/topic/ingredients/prices', (payload) => {
    const message = payload as PriceMessage;
    void queryClient.invalidateQueries({ queryKey: queryKeys.ingredients.all });
    // a price move changes every calculation that used it
    void queryClient.invalidateQueries({ queryKey: queryKeys.dishes.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.menus.all });
    notify({
      severity: 'warning',
      messageKey: 'realtime.priceChanged',
      values: { name: message.ingredientName },
    });
  });

  useTopic('/user/queue/notifications', (payload) => {
    const message = payload as { title: string; message: string };
    notify({ severity: 'info', message: `${message.title}: ${message.message}` });
  });
};
